/*------------------------------------------------*/
/* LIBRARIES
/*------------------------------------------------*/
import React, {
  Fragment,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useStaticQuery, graphql } from 'gatsby';
import _get from 'lodash/get';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
/*------------------------------------------------*/
/* INTERNAL DEPENDENCIES
/*------------------------------------------------*/
// Styles
import GlobalStyles from '../styles/GlobalStyles';
// Components
import Footer from './Footer';
// eslint-disable-next-line import/no-cycle
import Header from './Header/index';
import Container from './Container';
// Helpers
import {
  isBlogSection,
  isHelpCenterSection,
  isAuthorSection,
  isTrialSection,
} from '../utils/pageURLCheck';
import urlHelper from '../utils/url';
import generalHelper from '../utils/general';


if (!generalHelper.isWindowUndefined()) {
  // eslint-disable-next-line global-require
  require('smooth-scroll')('a[href*="#"]', {
    speed: 300,
    speedAsDuration: true,
  });
}

const Main = styled.main`
`;

const blogMenu = [
  { name: 'Product', url: '/blog/product/' },
  { name: 'Best Practices', url: '/blog/best-practices/' },
  { name: 'Customer Stories', url: '/blog/customer-stories/' },
  { name: 'Business Tips', url: '/blog/business-tips/' },
];
const helpMenu = [
  { name: 'Getting started', url: urlHelper.getHelpCategory('getting-started') },
  { name: 'How to guides', url: urlHelper.getHelpCategory('how-to-guides') },
  { name: 'With Slack', url: urlHelper.getHelpCategory('slack') },
  { name: 'Account & billing', url: urlHelper.getHelpCategory('account-and-billing') },
  { name: 'Mobile app', url: urlHelper.getHelpCategory('mobile-app') },
  { name: 'Extensions', url: urlHelper.getHelpCategory('extensions') }
];

const getMainMenu = (solutionItems) => {
  const mainMenu = [
    {
      name: 'Product',
      options: [
        { name: 'Features', url: '/product/' },
        { name: 'With Slack', url: '/slack/' },
        { name: 'With Webex', url: '/webex/' },
        { name: 'Extensions', url: '/extensions/' },
      ]
    },
    {
      name: 'Resources',
      options: [
        { name: 'Templates Library', url: '/templates/' },
        { name: 'Help Center', url: '/help/' },
        { name: 'Blog', url: '/blog/' },
        { name: 'API Docs', url: 'https://developers.hades.tech/' },
      ]
    },
    { name: 'Pricing', url: '/pricing/' },
  ];
  const mainMenuUpdated = [...mainMenu];

  const options = solutionItems.map(item => ({
    name: _get(item, 'team', ''),
    url: urlHelper.getSolutionArticle(_get(item, 'slug', '')),
  }));
  options.sort((itemA, itemB) => (itemA.name > itemB.name ? 1 : -1));

  // If there are "Solution" items, then we add it to the array:
  if (options.length > 0) {
    mainMenuUpdated.splice(1, 0, {
      name: 'Solutions',
      options,
    });
  }

  return mainMenuUpdated;
};

const getMenu = (location, extraData) => {
  const { solutionItems } = extraData;

  if (isBlogSection(location.pathname) || isAuthorSection(location.pathname)) {
    return blogMenu;
  }
  if (isHelpCenterSection(location.pathname)) {
    return helpMenu;
  }
  if (isTrialSection(location.pathname)) {
    return null;
  }
  return getMainMenu(solutionItems);
};

const Layout = ({
  location,
  children,
  useDarkBackground,
  useDarkBackgroundWithScroll,
  showScrollProgressBar,
  title,
  showHeader,
  showFooter,
  isEmbededView,
}) => {
  const data = useStaticQuery(
    graphql`
      query MyQueryForLayout {
        allContentfulSolutionPage {
          nodes {
            slug
            team
          }
        }
      }
    `
  );

  const solutionItems = _get(data, 'allContentfulSolutionPage.nodes', []);
  const extraData = {
    solutionItems,
  };
  const calculatedMenu = getMenu(location, extraData);

  const headerComponent = (
    <Header
      pathname={location.pathname}
      menu={calculatedMenu}
      useDarkBackground={useDarkBackground}
      useDarkBackgroundWithScroll={useDarkBackgroundWithScroll}
      showScrollProgressBar={showScrollProgressBar}
      title={title}
    />
  );
  const useContainer = isBlogSection(location.pathname) || isAuthorSection(location.pathname);
  const childrenComponent = useContainer
    ? <Container>{children}</Container>
    : <Fragment>{children}</Fragment>;
  return (
    <Fragment>
      <GlobalStyles
        theme="light"
        isEmbededView={isEmbededView}
      />
      {showHeader && headerComponent}
      <Main>
        {childrenComponent}
      </Main>
      {showFooter && (
        <Footer />
      )}
    </Fragment>
  );
};

/*------------------------------------------------*/
/* PROP TYPES
/*------------------------------------------------*/
Layout.defaultProps = {
  useDarkBackground: false,
  useDarkBackgroundWithScroll: false,
  showScrollProgressBar: false,
  title: '',
  showHeader: true,
  showFooter: true,
  isEmbededView: false,
};

Layout.propTypes = {
  // Required props:
  children: PropTypes.node.isRequired,
  location: PropTypes.shape({
    host: PropTypes.string,
    hostname: PropTypes.string,
    href: PropTypes.string,
    key: PropTypes.string,
    origin: PropTypes.string,
    pathname: PropTypes.string,
    port: PropTypes.string,
  }).isRequired,
  // Optional props:
  useDarkBackground: PropTypes.bool,
  useDarkBackgroundWithScroll: PropTypes.bool,
  showScrollProgressBar: PropTypes.bool,
  title: PropTypes.string,
  showHeader: PropTypes.bool,
  showFooter: PropTypes.bool,
  isEmbededView: PropTypes.bool,
};

/*------------------------------------------------*/
/* EXPORTS
/*------------------------------------------------*/
export default Layout;
